// FILE: components/study/ExamFullscreen.tsx
"use client";

import { useCallback } from "react";
import StudyFullscreenShell, {
  type ReviewData,
  type ResumeFromDraft,
  type SubmitPayload,
} from "./StudyFullscreenShell";
import { type Chapter, type StudyQuestion } from "./studyData";
import { buildQuestionAccessMap, type QuestionAccess } from "@/lib/path-access";
import { useDiamonds } from "@/lib/DiamondsContext";
import PurchaseConfirmModal from "@/components/shared/PurchaseConfirmModal";

interface Props {
  onExit?: () => void;
  examId?: string | null;
  reviewData?: ReviewData | null;
  resumeFromDraft?: ResumeFromDraft | null;
}

// ── Exam access map: dùng chung pattern Path (per-section demo_count + lock_mode) ──
// Chapter ở Exam là section (id = "section-{name}"). demo_count + lock_mode đã được
// merge từ exams.sections_meta vào Chapter ở getExamStudyMeta.
function buildExamAccessMap(
  chapters: Chapter[],
  questions: StudyQuestion[],
  isOwned: boolean
): Map<string, QuestionAccess> {
  return buildQuestionAccessMap(chapters, questions, isOwned);
}

export default function ExamFullscreen({ onExit, examId, reviewData, resumeFromDraft }: Props) {
  const { diamonds } = useDiamonds();
  const fetchMeta = useCallback(async () => {
    if (!examId) return null;
    const { getExamStudyMeta } = await import("@/actions/study");
    return getExamStudyMeta(examId);
  }, [examId]);

  const fetchChunk = useCallback(async (ids: string[]) => {
    if (!examId) return [];
    const { getExamStudyChunk } = await import("@/actions/study");
    return getExamStudyChunk(examId, ids);
  }, [examId]);

  const onSaveDraft = useCallback(async (data: { currentIndex: number; answers: Array<{ question_id: string; selected: string | null }>; timeSpent: number }) => {
    if (!examId) return;
    const { saveExamDraft } = await import("@/actions/exam-drafts");
    await saveExamDraft({
      examId,
      currentIndex: data.currentIndex,
      answers: data.answers,
      timeSpent: data.timeSpent,
    });
  }, [examId]);

  const onDeleteDraft = useCallback(async () => {
    if (!examId) return;
    const { deleteExamDraft } = await import("@/actions/exam-drafts");
    await deleteExamDraft(examId);
  }, [examId]);

  const onSubmitToServer = useCallback(async (payload: SubmitPayload) => {
    if (!examId) return;
    await fetch("/api/exam-submissions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        examId,
        ...payload,
      }),
    });
  }, [examId]);

  return (
    <StudyFullscreenShell
      kind="exam"
      itemId={examId ?? null}
      onExit={onExit}
      fetchMeta={fetchMeta}
      fetchChunk={fetchChunk}
      buildAccessMap={buildExamAccessMap}
      mindmapProps={{ examId }}
      editorUrl="/creator-studio"
      reviewData={reviewData}
      resumeFromDraft={resumeFromDraft}
      onSaveDraft={onSaveDraft}
      onDeleteDraft={onDeleteDraft}
      onSubmitToServer={onSubmitToServer}
      PurchaseConfirmModal={PurchaseConfirmModal}
      userBalance={diamonds}
    />
  );
}